// Goods In › Sourcing labels (Gabe 2026-11-03). JLI is the importer of record
// for stock bought through Flow Sourcing, so every unit leaves Goods In with:
//
//   label    (non-barcoded products) CODE128 of the variant SKU, the SKU,
//            the title + variant, the batch (the PO ref) and the importer
//            block — the barcode the warehouse scans from then on.
//   sticker  (barcoded products, whose supplier barcode was mapped) the same
//            text without our barcode, so the importer marking still goes on.
//
// One job carries every copy (the ordered quantity by default). ZPL to the
// desk's Zebra first — network relay, then the local agent's /print-zpl
// (agent ≥ 1.6.0) — and the HTML label through the local agent otherwise.
import { getSavedLocalPrinter, silentPrint } from "./printAgent";
import { printZplWithCascade, type PrintMode, type PrintResult } from "./carrierLabelPrint";

export type SourcingLabelKind = "label" | "sticker";

export interface SourcingLabelData {
    sku: string;
    title: string | null;
    variantLabel: string | null;
    batch: string;
}

export interface SourcingImporter {
    name: string | null;
    address: string | null;
    contact: string | null;
}

/** The printed words, translated by the caller (t()). */
export interface SourcingLabelText {
    importedBy: string; // "Imported by"
    batch: string;      // "Batch"
}

// 65 × 38 mm stock at 203 dpi (8 dots/mm), the same stock as the SKU labels.
export const SOURCING_LABEL_MM = { widthMm: 65, heightMm: 38 } as const;
const DOTS_PER_MM = 8;

/** "Robe – Grey / M", or just the title, or "". */
export const productLine = (d: Pick<SourcingLabelData, "title" | "variantLabel">): string =>
    [d.title?.trim(), d.variantLabel?.trim()].filter(Boolean).join(" – ");

// One printed line of description: long Flow titles wrapped into the batch
// and importer text, so the title is cut (the variant is what tells units apart).
export const PRODUCT_LINE_MAX = 32;
const clip = (s: string, max: number) => s.length <= max ? s : `${s.slice(0, Math.max(1, max - 1)).trimEnd()}…`;

/** productLine() cut to PRODUCT_LINE_MAX characters, keeping the variant whole. */
export const shortProductLine = (d: Pick<SourcingLabelData, "title" | "variantLabel">): string => {
    const full = productLine(d);
    if (full.length <= PRODUCT_LINE_MAX) return full;
    const title = d.title?.trim() ?? "";
    const variant = d.variantLabel?.trim() ?? "";
    if (!title || !variant) return clip(full, PRODUCT_LINE_MAX);
    const room = PRODUCT_LINE_MAX - variant.length - 3; // " – "
    return room >= 6 ? `${clip(title, room)} – ${variant}` : clip(full, PRODUCT_LINE_MAX);
};

/** "Imported by: JLI Trading LTD, Units 10-13 …, SA72 4RZ · contact". */
export const importerLine = (importer: SourcingImporter, text: SourcingLabelText): string => {
    const who = [importer.name, importer.address].map(s => s?.trim()).filter(Boolean).join(", ");
    const contact = importer.contact?.trim();
    if (!who && !contact) return "";
    return `${text.importedBy}: ${[who, contact].filter(Boolean).join(" · ")}`;
};

// ZPL field data: ^ and ~ are command prefixes; ^CI28 makes the rest UTF-8.
const zplText = (s: string) => s.replace(/[\^~]/g, " ").replace(/\s+/g, " ").trim();

/** Module width (dots) that keeps a CODE128 of `value` inside `maxDots`:
 *  subset B is ~11 modules a character plus ~35 for start/check/stop. */
export const code128ModuleWidth = (value: string, maxDots: number): number => {
    const modules = 11 * value.length + 35;
    return Math.max(1, Math.min(3, Math.floor(maxDots / modules)));
};

export function buildSourcingZpl(
    kind: SourcingLabelKind,
    data: SourcingLabelData,
    importer: SourcingImporter,
    text: SourcingLabelText,
): string {
    const W = SOURCING_LABEL_MM.widthMm * DOTS_PER_MM;
    const H = SOURCING_LABEL_MM.heightMm * DOTS_PER_MM;
    const pad = 16;
    const inner = W - pad * 2;
    const sku = zplText(data.sku);
    const product = zplText(shortProductLine(data));
    const batch = zplText(`${text.batch}: ${data.batch}`);
    const imp = zplText(importerLine(importer, text));
    const out: string[] = ["^XA", "^CI28", `^PW${W}`, `^LL${H}`, "^LH0,0"];
    let y = 12;
    if (kind === "label") {
        const by = code128ModuleWidth(sku, inner);
        const barsW = (11 * sku.length + 35) * by;
        const x = Math.max(pad, Math.round((W - barsW) / 2));
        out.push(`^FO${x},${y}^BY${by}^BCN,84,N,N,N^FD${sku}^FS`);
        y += 90;
        out.push(`^FO0,${y}^A0N,30,30^FB${W},1,0,C^FD${sku}^FS`);
        y += 34;
    } else {
        out.push(`^FO0,${y}^A0N,34,34^FB${W},1,0,C^FD${sku}^FS`);
        y += 42;
    }
    if (product) {
        out.push(`^FO${pad},${y}^A0N,22,22^FB${inner},1,0,C^FD${product}^FS`);
        y += 28;
    }
    out.push(`^FO${pad},${y}^A0N,22,22^FB${inner},1,0,C^FD${batch}^FS`);
    y += 28;
    if (imp) {
        const lines = Math.max(1, Math.floor((H - y - 6) / 20));
        out.push(`^FO${pad},${y}^A0N,18,18^FB${inner},${lines},2,C^FD${imp}^FS`);
    }
    out.push("^XZ");
    return out.join("\n");
}

const esc = (s: string) => s.replace(/[<>&"]/g, c => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "\"": "&quot;" }[c] as string));
// Safe inside a <script>: JSON quoting plus no way to close the tag.
const jsString = (s: string) => JSON.stringify(s).replace(/</g, "\\u003c");

/** HTML fallback for non-Zebra printers: same content as the ZPL. */
export function buildSourcingHtml(
    kind: SourcingLabelKind,
    data: SourcingLabelData,
    importer: SourcingImporter,
    text: SourcingLabelText,
): string {
    const { widthMm: w, heightMm: h } = SOURCING_LABEL_MM;
    const product = shortProductLine(data);
    const imp = importerLine(importer, text);
    const bars = kind === "label";
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: ${w}mm ${h}mm; margin: 0; }
html, body { width: ${w}mm; height: ${h}mm; }
body {
    padding: 1.5mm 2mm;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    font-family: Arial, Helvetica, sans-serif;
    color: #000;
    background: #fff;
    text-align: center;
    overflow: hidden;
}
svg { width: ${w - 6}mm !important; height: 11mm !important; display: block; }
.sku { font-size: ${bars ? 13 : 16}px; font-weight: 700; font-family: monospace; letter-spacing: 0.05em; margin-top: 0.5mm; }
.product { font-size: 9px; line-height: 1.15; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 0.5mm; }
.batch { font-size: 9px; font-weight: 700; margin-top: 0.5mm; }
.importer { font-size: 7.5px; line-height: 1.2; margin-top: 0.5mm; }
</style>
${bars ? `<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>` : ""}
</head>
<body>
${bars ? `<svg id="barcode"></svg>` : ""}
<div class="sku">${esc(data.sku)}</div>
${product ? `<div class="product">${esc(product)}</div>` : ""}
<div class="batch">${esc(`${text.batch}: ${data.batch}`)}</div>
${imp ? `<div class="importer">${esc(imp)}</div>` : ""}
${bars ? `<script>
JsBarcode("#barcode", ${jsString(data.sku)}, { format: "CODE128", width: 2, height: 60, displayValue: false, margin: 0 });
document.getElementById("barcode").setAttribute("preserveAspectRatio", "none");
<\/script>` : ""}
</body>
</html>`;
}

/** Print `copies` of one Sourcing label or sticker in a single job. */
export async function printSourcingLabels(
    kind: SourcingLabelKind,
    data: SourcingLabelData,
    importer: SourcingImporter,
    text: SourcingLabelText,
    copies: number,
    mode: PrintMode,
): Promise<PrintResult> {
    const n = Math.max(1, Math.floor(copies));
    const zpl = await printZplWithCascade(buildSourcingZpl(kind, data, importer, text), mode, n);
    if (zpl.success) return zpl;
    const html = await silentPrint(buildSourcingHtml(kind, data, importer, text), n, getSavedLocalPrinter() ?? undefined);
    return html.success ? html : { success: false, error: html.error || zpl.error };
}
